import { useState, useCallback } from "react";
import documentSettingsService from "@/services/document-settings.service";
import {
  DocumentSettings,
  UpdateDocumentSettingsDto,
  DocumentSettingsFlag,
} from "@/types/document-settings";

export const useDocumentSettings = (memberId: string) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Get settings for a single document
   */
  const getSettings = useCallback(
    async (documentId: string): Promise<DocumentSettings | null> => {
      try {
        setLoading(true);
        setError(null);
        const settings = await documentSettingsService.getSettings(
          memberId,
          documentId
        );
        return settings;
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [memberId]
  );

  /**
   * Get settings for multiple documents
   */
  const getBulkSettings = useCallback(
    async (documentIds: string[]): Promise<DocumentSettings[]> => {
      try {
        setLoading(true);
        setError(null);
        const settings = await documentSettingsService.getBulkSettings(
          memberId,
          documentIds
        );
        return settings;
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [memberId]
  );

  /**
   * Update settings for a document
   */
  const updateSettings = useCallback(
    async (
      documentId: string,
      settings: UpdateDocumentSettingsDto
    ): Promise<DocumentSettings> => {
      try {
        setLoading(true);
        setError(null);
        const updated = await documentSettingsService.updateSettings(
          memberId,
          documentId,
          settings
        );
        return updated;
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [memberId]
  );

  /**
   * Update a specific flag
   */
  const updateFlag = useCallback(
    async (
      documentId: string,
      flag: DocumentSettingsFlag,
      value: boolean
    ): Promise<DocumentSettings> => {
      try {
        setLoading(true);
        setError(null);
        const updated = await documentSettingsService.updateFlag(
          memberId,
          documentId,
          flag,
          value
        );
        return updated;
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [memberId]
  );

  /**
   * Delete settings for a document
   */
  const deleteSettings = useCallback(
    async (documentId: string): Promise<void> => {
      try {
        setLoading(true);
        setError(null);
        await documentSettingsService.deleteSettings(memberId, documentId);
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [memberId]
  );

  /**
   * Helper to get settings with defaults
   */
  const getSettingsWithDefaults = useCallback(
    async (documentId: string, ownerId: string): Promise<DocumentSettings> => {
      const settings = await getSettings(documentId);
      if (settings) {
        return settings;
      }
      // Return default settings if none exist
      return {
        id: "",
        documentId,
        memberId,
        ownerId,
        availableOffline: false,
        isImportant: false,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
    },
    [getSettings, memberId]
  );

  return {
    loading,
    error,
    getSettings,
    getBulkSettings,
    updateSettings,
    updateFlag,
    deleteSettings,
    getSettingsWithDefaults,
  };
};
